import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { api } from "../lib/api";
import { featureFlags } from "@/config/featureFlags";
import { getSchoolSlug, isSchoolMode, buildSyntheticEmail } from "@/lib/school";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, ArrowLeft, Lock, Eye, EyeOff } from "lucide-react";
import logo from "@/assets/ithopu-logo.png";
import iTHOPULogo from "@/assets/iTHOPU.png";

export default function Login() {
  const schoolSlug = getSchoolSlug();
  const schoolMode = isSchoolMode();
  const [userId, setUserId] = useState(""); // For school mode
  const [schoolInfo, setSchoolInfo] = useState<{ name: string; logo_url: string | null } | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState<"password" | "otp">(
    "password",
  );
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const navigate = useNavigate();

  // Fetch school info for school mode
  useEffect(() => {
    if (schoolSlug) {
      api.getSchoolBySlug(schoolSlug)
        .then((info) => setSchoolInfo(info))
        .catch(() => setSchoolInfo(null));
    }
  }, [schoolSlug]);

  const handlePendingCheckout = async () => {
    if (!featureFlags.CHARGEBEE_BILLING) return false;
    const pendingPlan = sessionStorage.getItem("checkout_plan");
    if (pendingPlan) {
      sessionStorage.removeItem("checkout_plan");
      try {
        const { url } = await api.createCheckoutSession(pendingPlan);
        window.location.href = url;
        return true;
      } catch {
        // If checkout fails, just go to dashboard
      }
    }
    return false;
  };

  const handlePasswordLogin = async () => {
    console.log("=== handlePasswordLogin called ===");

    // In school mode, construct synthetic email from userId
    const loginEmail = schoolMode && schoolSlug
      ? buildSyntheticEmail(userId, schoolSlug)
      : email;

    console.log("Email:", loginEmail);

    if (schoolMode ? !userId || !password : !email || !password) {
      setError(schoolMode ? "Please enter both User ID and password" : "Please enter both email and password");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const { data, error: loginError } =
        await supabase.auth.signInWithPassword({
          email: loginEmail,
          password,
        });

      if (loginError) throw loginError;

      console.log("Password login successful!", data);
      const redirected = await handlePendingCheckout();
      if (!redirected) navigate("/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (errorMessage.toLowerCase().includes("invalid login credentials")) {
        setError(schoolMode
          ? "Invalid User ID or password. Please try again."
          : "Invalid email or password. Please try again.");
      } else {
        setError(errorMessage || "Failed to login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    console.log("=== handleForgotPassword called ===");
    console.log("Email:", email);

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email,
        {
          redirectTo: `${window.location.origin}/reset-password`,
        },
      );

      if (resetError) throw resetError;

      console.log("Password reset email sent!");
      setSuccess("Password reset link sent! Check your email.");
      setResetEmailSent(true);
    } catch (err) {
      console.error("Password reset error:", err);
      setError(
        err instanceof Error ? err.message : "Failed to send reset email",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setResetEmailSent(false);
    setError("");
    setSuccess("");
  };

  const handleSendOTP = async () => {
    console.log("=== handleSendOTP called ===");
    console.log("Email:", email);

    if (!email) {
      console.log("Validation failed - missing email");
      setError("Please enter your email");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);
    console.log("Sending OTP...");

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false, // Only allow existing users to login
        },
      });

      if (otpError) throw otpError;

      console.log("OTP sent successfully!");
      setSuccess("Check your email for the verification code!");
      setOtpSent(true);
    } catch (err) {
      console.error("OTP error caught:", err);
      console.error(
        "Error message:",
        err instanceof Error ? err.message : String(err),
      );

      // Handle specific error case for non-existent users
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (
        errorMessage.toLowerCase().includes("signups not allowed") ||
        errorMessage.toLowerCase().includes("user not found")
      ) {
        setError(
          "Account not found. Please register first to create an account.",
        );
      } else {
        setError(errorMessage || "Failed to send OTP");
      }
    } finally {
      console.log("OTP send process complete, setting loading to false");
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    console.log("=== handleVerifyOTP called ===");
    console.log("Email:", email);
    console.log("OTP length:", otp?.length);

    if (!otp) {
      console.log("Validation failed - missing OTP");
      setError("Please enter the verification code");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);
    console.log("Verifying OTP...");

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: "email",
      });

      if (verifyError) throw verifyError;

      console.log("OTP verified successfully!");
      const redirected = await handlePendingCheckout();
      if (!redirected) navigate("/dashboard");
    } catch (err) {
      console.error("Verify error caught:", err);
      console.error(
        "Error message:",
        err instanceof Error ? err.message : String(err),
      );
      setError(
        err instanceof Error ? err.message : "Invalid verification code",
      );
    } finally {
      console.log("OTP verify process complete, setting loading to false");
      setLoading(false);
    }
  };

  const handleBack = () => {
    setOtpSent(false);
    setOtp("");
    setError("");
    setSuccess("");
  };

  const handleGoogleLogin = async () => {
    console.log("=== handleGoogleLogin called ===");
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const { error: googleError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: sessionStorage.getItem("checkout_plan")
            ? `${window.location.origin}/checkout/success`
            : `${window.location.origin}/dashboard`,
        },
      });

      if (googleError) throw googleError;

      // OAuth redirect will happen automatically
      console.log("Google OAuth initiated");
    } catch (err) {
      console.error("Google login error:", err);
      setError(
        err instanceof Error ? err.message : "Failed to login with Google",
      );
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (loginMethod === "password") {
        handlePasswordLogin();
      } else if (otpSent) {
        handleVerifyOTP();
      } else {
        handleSendOTP();
      }
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 flex flex-col items-center">
          {schoolMode && schoolInfo?.logo_url ? (
            <img src={schoolInfo.logo_url} alt={schoolInfo.name} className="w-16 h-16 mb-2 object-contain" />
          ) : (
            <div className="flex items-center gap-2 mb-2">
              <img src={logo} alt="Ithopu" className="w-12 h-12" />
              <img src={iTHOPULogo} alt="Ithopu" className="h-10" />
            </div>
          )}
          <CardDescription>
            {schoolMode && schoolInfo
              ? `Welcome to ${schoolInfo.name}`
              : otpSent
                ? "Enter your verification code"
                : "Login to continue your math journey"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {!otpSent ? (
            <>
              {schoolMode ? (
                /* School mode: simple User ID + Password login */
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="user-id">User ID</Label>
                    <Input
                      id="user-id"
                      type="text"
                      placeholder="Enter your User ID"
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={handlePasswordLogin}
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? "Logging in..." : "Login"}
                  </Button>
                  <p className="text-xs text-center text-gray-500">
                    Contact your teacher if you forgot your password
                  </p>
                </div>
              ) : (
              <>
              <Tabs
                value={loginMethod}
                onValueChange={(v) => setLoginMethod(v as "password" | "otp")}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="password">
                    <Lock className="w-4 h-4 mr-2" />
                    Password
                  </TabsTrigger>
                  <TabsTrigger value="otp">
                    <Mail className="w-4 h-4 mr-2" />
                    Email Code
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="password" className="space-y-4 mt-4">
                  {!showForgotPassword ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="email-password">Email</Label>
                        <Input
                          id="email-password"
                          type="email"
                          placeholder="name@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onKeyPress={handleKeyPress}
                          disabled={loading}
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password">Password</Label>
                          <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline"
                          >
                            Forgot password?
                          </button>
                        </div>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={handlePasswordLogin}
                        className="w-full"
                        disabled={loading}
                      >
                        {loading ? "Logging in..." : "Login"}
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="reset-email">Email</Label>
                        <Input
                          id="reset-email"
                          type="email"
                          placeholder="name@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={loading || resetEmailSent}
                        />
                        <p className="text-xs text-gray-500">
                          Enter your email to receive a password reset link
                        </p>
                      </div>
                      {!resetEmailSent ? (
                        <>
                          <Button
                            type="button"
                            onClick={handleForgotPassword}
                            className="w-full"
                            disabled={loading}
                          >
                            {loading ? "Sending..." : "Send Reset Link"}
                          </Button>
                          <Button
                            type="button"
                            onClick={handleBackToLogin}
                            variant="ghost"
                            className="w-full"
                            disabled={loading}
                          >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Login
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          onClick={handleBackToLogin}
                          variant="outline"
                          className="w-full"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Back to Login
                        </Button>
                      )}
                    </>
                  )}
                </TabsContent>

                <TabsContent value="otp" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="email-otp">Email</Label>
                    <Input
                      id="email-otp"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500">
                      We'll send you a one-time code to login
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleSendOTP}
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Send Verification Code"}
                  </Button>
                </TabsContent>
              </Tabs>

              {/* Google Sign-In Separator */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">
                    Or continue with
                  </span>
                </div>
              </div>

              {/* Google Sign-In Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleLogin}
                disabled={loading}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {loading ? "Signing in..." : "Sign in with Google"}
              </Button>
            </>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="otp">Verification Code</Label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                maxLength={10}
                className="text-center text-lg tracking-widest"
              />
              <p className="text-xs text-gray-500 mt-1">
                Check your email for the verification code
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          {otpSent && (
            <div className="w-full space-y-2">
              <Button
                type="button"
                onClick={handleVerifyOTP}
                className="w-full"
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify & Login"}
              </Button>
              <Button
                type="button"
                onClick={handleBack}
                variant="ghost"
                className="w-full"
                disabled={loading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          )}
          {!schoolMode && (
            <div className="text-sm text-center text-gray-600 space-y-2">
              <span>Don't have an account?</span>
              <Button className="w-full" onClick={() => navigate("/register")}>
                Sign Up
              </Button>
            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
